import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import {
  ClientReferenceNumberType,
  Loadable,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';
import { useParentEntityStore } from './ParentEntity';

export interface LoadReferenceNumberTypesOptions {
  bypassCache?: boolean;
  level: ReferenceNumberTypeLevel;
}

export interface ReferenceNumberTypeStore {
  shipmentReferenceNumberTypes: ComputedRef<
    Loadable<ClientReferenceNumberType[]>
  >;
  orderReferenceNumberTypes: ComputedRef<Loadable<ClientReferenceNumberType[]>>;
  lineItemReferenceNumberTypes: ComputedRef<
    Loadable<ClientReferenceNumberType[]>
  >;
  loadReferenceNumberTypes: (
    opts: LoadReferenceNumberTypesOptions,
  ) => Promise<void>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS = {
  bypassCache: false,
};

type RefNums = Loadable<ClientReferenceNumberType[]>;

const _useReferenceNumberTypeStore = defineStore(
  'referenceNumberType',
  (): ReferenceNumberTypeStore => {
    const { selectedDomainId } = useCurrentUserStore();
    const { t } = useI18n();
    const { includeSubDomains } = useParentEntityStore();
    const toast = useToast();

    const _shipmentReferenceNumberTypes: Ref<RefNums> = ref({ loading: false });
    const _orderReferenceNumberTypes: Ref<RefNums> = ref({ loading: false });
    const _lineItemReferenceNumberTypes: Ref<RefNums> = ref({ loading: false });

    const shipmentReferenceNumberTypes = computed(
      () => _shipmentReferenceNumberTypes.value,
    );
    const orderReferenceNumberTypes = computed(
      () => _orderReferenceNumberTypes.value,
    );
    const lineItemReferenceNumberTypes = computed(
      () => _lineItemReferenceNumberTypes.value,
    );

    const loadReferenceNumberTypes = async (
      opts: LoadReferenceNumberTypesOptions,
    ) => {
      if (!authorization.hasAuthority('client', 'read')) {
        return;
      }

      const options: Required<LoadReferenceNumberTypesOptions> = {
        ...PERFORM_LOAD_DEFAULT_OPTIONS,
        ...opts,
      };

      let whichRefNumType: Ref<RefNums>;
      let load: () => Promise<void>;
      switch (options.level) {
        case ReferenceNumberTypeLevel.SHIPMENT:
          whichRefNumType = _shipmentReferenceNumberTypes;
          load = loadShipmentReferenceNumberTypes;
          break;
        case ReferenceNumberTypeLevel.ORDER:
          whichRefNumType = _orderReferenceNumberTypes;
          load = loadOrderReferenceNumberTypes;
          break;
        case ReferenceNumberTypeLevel.LINE_ITEM:
          whichRefNumType = _lineItemReferenceNumberTypes;
          load = loadLineItemReferenceNumberTypes;
          break;
        default:
          throw new Error(`Invalid ReferenceNumberTypeLevel: ${options.level}`);
      }

      const numRefNumTypes = whichRefNumType.value.data?.length ?? 0;
      const isLoading = whichRefNumType.value.loading;
      const requiresLoad = numRefNumTypes === 0 && !isLoading;

      if (options.bypassCache || requiresLoad) {
        return load();
      }
    };

    const handleError = (e: unknown, level: ReferenceNumberTypeLevel) => {
      console.error(
        `Error loading reference number types for level: ${level}`,
        e,
      );
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('common.paramTypes', {
            param: t('referenceData.referenceNumbers'),
            count: 2,
          }),
        }),
        error: e,
      });
    };

    const loadShipmentReferenceNumberTypes = async () => {
      try {
        _shipmentReferenceNumberTypes.value.loading = true;
        _shipmentReferenceNumberTypes.value.data =
          await api.referenceNumberType.findAllShipmentTypes();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        _shipmentReferenceNumberTypes.value.error = e;
        handleError(e, ReferenceNumberTypeLevel.SHIPMENT);
      } finally {
        _shipmentReferenceNumberTypes.value.loading = false;
      }
    };

    const loadOrderReferenceNumberTypes = async () => {
      try {
        _orderReferenceNumberTypes.value.loading = true;
        _orderReferenceNumberTypes.value.data =
          await api.referenceNumberType.findAllOrderTypes();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        _orderReferenceNumberTypes.value.error = e;
        handleError(e, ReferenceNumberTypeLevel.ORDER);
      } finally {
        _orderReferenceNumberTypes.value.loading = false;
      }
    };

    const loadLineItemReferenceNumberTypes = async () => {
      try {
        _lineItemReferenceNumberTypes.value.loading = true;
        _lineItemReferenceNumberTypes.value.data =
          await api.referenceNumberType.findAllLineItemTypes();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        _lineItemReferenceNumberTypes.value.error = e;
        handleError(e, ReferenceNumberTypeLevel.LINE_ITEM);
      } finally {
        _lineItemReferenceNumberTypes.value.loading = false;
      }
    };

    const reset = () => {
      _shipmentReferenceNumberTypes.value.data = [];
      _orderReferenceNumberTypes.value.data = [];
      _lineItemReferenceNumberTypes.value.data = [];
    };

    watch(selectedDomainId, () => {
      reset();
    });

    watch(includeSubDomains, () => {
      reset();
    });

    return {
      shipmentReferenceNumberTypes,
      orderReferenceNumberTypes,
      lineItemReferenceNumberTypes,
      loadReferenceNumberTypes,
    };
  },
);

export const useReferenceNumberTypeStore = (): ReferenceNumberTypeStore => {
  const store = _useReferenceNumberTypeStore();
  const {
    shipmentReferenceNumberTypes,
    orderReferenceNumberTypes,
    lineItemReferenceNumberTypes,
  } = storeToRefs(store);
  const { loadReferenceNumberTypes } = store;

  return {
    shipmentReferenceNumberTypes,
    orderReferenceNumberTypes,
    lineItemReferenceNumberTypes,
    loadReferenceNumberTypes,
  };
};
